/**
 * Scene 3: precheck, artifact diff, and global references.
 *
 * Starts the precheck whenever the bucket set (Werke plus package) changes,
 * and builds the table rows from state.precheckByBucket.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, buckets, visBuckets, activeWerke } from "../context/TransportV2Context";
import { usePrecheckStream } from "../hooks/usePrecheckStream";
import { api } from "../../../utils/helpers";
import BpmnCompareDialog from "../../../components/BpmnCompareDialog";

function mapStatus(s) {
  if (s === "new") return "new";
  if (s === "update") return "update";
  if (s === "identical") return "identical";
  if (s === "draft") return "draft";
  if (s === "orphan") return "orphan";
  return s || "identical";
}

const ST_CLS = { new: "ok", update: "info", identical: "idle", draft: "warn", orphan: "warn" };

function cmpVer(a, b) {
  const A = String(a || "").split(".").map((n) => parseInt(n, 10) || 0);
  const B = String(b || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const d = (A[i] || 0) - (B[i] || 0);
    if (d) return d;
  }
  return 0;
}

export default function Scene3Artifacts() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const stream = usePrecheckStream();
  const [modal, setModal] = useState(null);
  const [release, setRelease] = useState(null);
  const startedRef = useRef(null);

  const bucketList = buckets(state);
  const visible = visBuckets(state);
  const withWerke = hasWerke(state);

  // Runs the precheck once per distinct bucket signature (space + package +
  // active Werke). startedRef guards against re-triggering on unrelated
  // re-renders.
  useEffect(() => {
    if (state.step !== 3) return;
    if (!state.sourceInstanceId || !state.pkgId) return;
    const sig = state.spaceKey + "|" + state.pkgId + "|" + bucketList.map((b) => b.key).join(",");
    if (startedRef.current === sig) return;
    startedRef.current = sig;
    stream.reset();
    const werke = withWerke ? bucketList.map((b) => ({ key: b.key, kennung: b.kennung, name: b.name })) : [];
    const targetInst = state.space?.qasInstance;
    if (!targetInst) return;
    stream.run({
      sourceInstanceId: state.sourceInstanceId,
      sourcePackageId: state.pkgId,
      targetInstances: [targetInst],
      werke,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.pkgId, state.spaceKey, state.werkOn]);

  // Mirror the precheck result into context state and pre-select rows.
  useEffect(() => {
    if (!stream.precheck) return;
    if (Object.keys(stream.precheck).length === 0) return;
    dispatch({ type: "SET_PRECHECK", payload: stream.precheck });
    dispatch({ type: "SET_GLOBAL_REFS", payload: stream.refs || [] });
    // Default selection per Werk: everything except identical or draft
    // artifacts.
    const sel = {};
    const expanded = {};
    for (const entry of Object.values(stream.precheck)) {
      if (!entry || !Array.isArray(entry.comparison)) continue;
      const werkKey = entry.werk?.kennung || "std";
      for (const a of entry.comparison) {
        const src = a.originalId || a.Id;
        if (!sel[src]) sel[src] = {};
        expanded[src] = true;
        const status = mapStatus(a.status);
        if (status !== "identical" && !a.draft) sel[src][werkKey] = true;
      }
    }
    dispatch({ type: "SET_SEL", payload: sel });
    dispatch({ type: "SET_EXPANDED", payload: expanded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.precheck, stream.refs]);

  // Aggregate precheck entries into one row per source artifact, with a
  // werkeStatus entry per Werk.
  const rows = useMemo(() => {
    const cmp = state.precheckByBucket || {};
    const byOrigId = new Map();
    for (const [, entry] of Object.entries(cmp)) {
      if (!entry || !Array.isArray(entry.comparison)) continue;
      const werkKey = entry.werk?.kennung || "std";
      const werk = entry.werk ? { key: werkKey, name: entry.werk.name, kennung: entry.werk.kennung } : { key: "std", name: "Standard", kennung: "" };
      for (const a of entry.comparison) {
        const origId = a.originalId || a.Id;
        if (!byOrigId.has(origId)) {
          byOrigId.set(origId, {
            id: origId,
            name: a.Name || origId,
            type: a.Type || "Integration Flow",
            srcVer: a.Version || "-",
            draft: !!a.draft,
            lastVer: a.lastVer || a.Version || "1.0.0",
            werkeStatus: {},
            werke: [],
          });
        }
        const target = byOrigId.get(origId);
        target.werkeStatus[werkKey] = { status: mapStatus(a.status), tgt: a.targetVersion || null, suffixedId: a.suffixedId || a.Id, instanceId: entry.instance?.id };
        target.werke.push({ ...werk, ...target.werkeStatus[werkKey] });
      }
    }
    // Sort: draft artifacts first, then alphabetically.
    return [...byOrigId.values()].sort((a, b) => {
      if (a.draft !== b.draft) return a.draft ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [state.precheckByBucket]);

  const selectedRowsCount = useMemo(() => {
    let n = 0;
    for (const [src, werkSel] of Object.entries(state.sel || {})) {
      for (const b of bucketList) {
        if (werkSel && werkSel[b.key]) n++;
        void src;
      }
    }
    return n;
  }, [state.sel, bucketList]);

  const counts = useMemo(() => {
    const c = { new: 0, update: 0, identical: 0, draft: 0 };
    for (const row of rows) {
      if (row.draft) { c.draft += 1; continue; }
      for (const b of visible) {
        const info = row.werkeStatus[b.key];
        if (info) c[info.status] = (c[info.status] || 0) + 1;
      }
    }
    return c;
  }, [rows, visible]);

  const openModal = (row, werkKey) => {
    const info = row.werkeStatus[werkKey];
    if (!info) return;
    setModal({
      artId: row.id,
      artName: row.name,
      sourceInstanceId: state.sourceInstanceId,
      targetInstanceId: info.instanceId,
      targetArtId: info.suffixedId,
      srcVer: row.srcVer,
      tgtVer: info.tgt,
    });
  };

  const openRelease = useCallback(async (row) => {
    setRelease({ artId: row.id, loading: "info", version: "", floor: null, error: null });
    try {
      const d = await api(`/api/${state.sourceInstanceId}/transport/artifact-last-version/${encodeURIComponent(row.id)}?packageId=${encodeURIComponent(state.pkgId)}`);
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, floor: d.floor || null, version: d.suggested || d.lastVersion || row.lastVer || "1.0.1" } : p));
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, version: row.lastVer || "1.0.1", error: e.message } : p));
    }
  }, [state.sourceInstanceId, state.pkgId]);

  const doRelease = useCallback(async (row) => {
    const r = release;
    if (!r || r.artId !== row.id) return;
    const ver = (r.version || "").trim();
    if (!/^\d+(\.\d+){1,3}$/.test(ver)) { setRelease({ ...r, error: "Ungültiges Format" }); return; }
    if (r.floor && cmpVer(ver, r.floor) <= 0) { setRelease({ ...r, error: `Version muss größer als ${r.floor} sein` }); return; }
    setRelease({ ...r, loading: "release", error: null });
    try {
      await api(`/api/${state.sourceInstanceId}/transport/release-version`, {
        method: "POST",
        body: JSON.stringify({ flowId: row.id, version: ver, packageId: state.pkgId, floor: r.floor || null }),
      });
      setRelease(null);
      // Force the precheck effect to run again by clearing startedRef (the
      // bucket signature itself did not change).
      startedRef.current = null;
      dispatch({ type: "SET_PRECHECK", payload: {} });
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, error: e.message } : p));
    }
  }, [release, state.sourceInstanceId, state.pkgId, dispatch]);

  const nSelectable = (row) => visible.filter((b) => {
    const info = row.werkeStatus[b.key];
    return info && info.status !== "identical";
  }).length;
  const nOn = (row) => visible.filter((b) => state.sel?.[row.id]?.[b.key]).length;

  const onToggle = (rowId, werkKey, on) => dispatch({ type: "TOGGLE_SEL", payload: { sourceId: rowId, werkKey, on } });

  const onParent = (row, on) => {
    for (const b of visible) {
      const info = row.werkeStatus[b.key];
      if (info && info.status !== "identical") onToggle(row.id, b.key, on);
    }
  };

  const StatusPill = ({ status }) => {
    const cls = ST_CLS[status] || "idle";
    const label = t("st." + status) || status;
    return <span className={"st st--" + cls}><i className="dot" />{label}</span>;
  };

  const refs = state.globalRefs || [];
  const refsMapped = useMemo(() => refs.map((r) => {
    const id = r?.bundleId || r?.id || r?.Name || r?.name || r?.Id;
    const name = r?.name || r?.Name || r?.bundleId || r?.id || r?.Id || "-";
    const type = r?.type || "Reference";
    const srcVer = r?.version || r?.srcVer || r?.srcVersion || r?.Version || "-";
    const tgtVer = r?.targetVersion || r?.tgtVer || "-";
    let statusRaw = r?.status;
    if (statusRaw) statusRaw = String(statusRaw).toLowerCase();
    else if (r?.targetVersion) statusRaw = r?.version === r?.targetVersion ? "identical" : "update";
    else statusRaw = "new";
    // resolvable=false: the reference does not exist on the source tenant.
    // The reducer already forces refSel to false; here we also disable the
    // checkbox in the UI and show a clear message.
    const resolvable = r?.resolvable !== false;
    return { id, name, type, srcVer, tgtVer, status: statusRaw, resolvable };
  }), [refs]);

  const canNext = selectedRowsCount > 0;

  return (
    <section className="scene active scene-enter" id="s3">
      <div className="sh">
        <h2>{t("s3.h2")}</h2>
        <p>{t("s3.p")}</p>
      </div>

      {(stream.running || stream.phases.some((p) => p.status === "active")) && (
        <div className="panel panel-pad">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>{t("s3.loading")}</h3>
            <span className="tiny muted">
              {withWerke ? t("s3.par", { n: activeWerke(state).length }) : t("s3.single")}
            </span>
          </div>
          <div>
            {[
              { id: "auth", label: t("s3.ph1") },
              { id: "source", label: t("s3.ph2") },
              { id: "target", label: t("s3.ph3") },
              { id: "refs", label: t("s3.ph4") },
              { id: "diff", label: t("s3.ph5") },
            ].map((ph) => {
              const it = stream.phases.find((x) => x.id === ph.id);
              const st = it?.status || "waiting";
              const cls = st === "done" ? "done" : st === "active" ? "run" : "";
              return (
                <div className={"phase-li " + cls} key={ph.id}>
                  <span className="ph-ind">
                    {st === "active" && <span className="spinner" />}
                    {st === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <span>{ph.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stream.error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.precheck")}: {stream.error}</span>
          <button className="btn btn-sec btn-sm" onClick={() => { startedRef.current = null; stream.reset(); }}>{t("err.retry")}</button>
        </div>
      )}

      {!stream.running && Object.keys(state.precheckByBucket || {}).length > 0 && (
        <>
          <div className="panel">
            <div className="panel-h">
              <h3>{t("s3.tableTitle")}</h3>
              <div className="diffcounts">
                {counts.update > 0 && <span style={{ color: "var(--info)" }}><b>{counts.update}</b> {t("st.update")}</span>}
                {counts.new > 0 && <span style={{ color: "var(--ok)" }}><b>{counts.new}</b> {t("st.new")}</span>}
                {counts.identical > 0 && <span className="muted"><b>{counts.identical}</b> {t("st.identical")}</span>}
                {counts.draft > 0 && <span style={{ color: "var(--warn)" }}><b>{counts.draft}</b> {t("st.draft")}</span>}
              </div>
            </div>
            {withWerke && (
              <div style={{ display: "block", padding: "14px 26px 0" }}>
                <div className="utabs" style={{ marginBottom: 0, borderBottom: "none" }}>
                  <button className={"utab" + (state.workingTab === "all" ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: "all" })}>
                    {t("s3.all", { n: bucketList.length })}
                  </button>
                  {bucketList.map((b) => (
                    <button key={b.key} className={"utab" + (state.workingTab === b.key ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: b.key })}>
                      {b.name} <span className="ken">{b.kennung}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
              <table className="dt">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>{t("th.art")}</th>
                    <th>{t("th.type")}</th>
                    <th>{t("th.src")}</th>
                    <th>{t("th.tgt")}</th>
                    <th>{t("th.status")}</th>
                    <th>{t("th.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    if (row.draft) {
                      const isEditing = release && release.artId === row.id;
                      return (
                        <tr key={row.id}>
                          <td />
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{t("s3.draftLast", { id: row.id, v: row.lastVer })}</span>
                          </td>
                          <td>{row.type}</td>
                          <td>{t("st.draft")}</td>
                          <td>-</td>
                          <td><StatusPill status="draft" /></td>
                          <td>
                            {!isEditing && (
                              <button className="link-act" onClick={() => openRelease(row)}>{t("s3.release")}</button>
                            )}
                            {isEditing && (
                              <span className="draft-editor">
                                <input
                                  className={"inp" + (release?.error ? " inp--invalid" : "")}
                                  value={release.version}
                                  onChange={(e) => setRelease({ ...release, version: e.target.value, error: null })}
                                  aria-label="Version"
                                />
                                <button className="btn btn-sm btn-sec" onClick={() => doRelease(row)} disabled={release.loading}>
                                  {t("s3.doRel")}
                                </button>
                                <span className="tiny muted">{t("s3.lastVer", { v: release.floor || row.lastVer })}</span>
                                {release.error && <span className="tiny" style={{ color: "var(--crit)" }}>{release.error}</span>}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    if (!withWerke) {
                      const info = row.werkeStatus.std;
                      if (!info) return null;
                      const ident = info.status === "identical";
                      const sel = !!state.sel?.[row.id]?.std;
                      return (
                        <tr key={row.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="cb"
                              checked={sel}
                              disabled={ident}
                              onChange={(e) => onToggle(row.id, "std", e.target.checked)}
                            />
                          </td>
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{row.id}</span>
                          </td>
                          <td>{row.type}</td>
                          <td>{row.srcVer}</td>
                          <td>{info.tgt || "-"}</td>
                          <td><StatusPill status={info.status} /></td>
                          <td>
                            {info.status === "update" && (
                              <button className="link-act" onClick={() => openModal(row, "std")}>{t("s3.view")}</button>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    const selectable = nSelectable(row);
                    const chosen = nOn(row);
                    const agg = {};
                    for (const b of visible) {
                      const info = row.werkeStatus[b.key];
                      if (!info) continue;
                      agg[info.status] = (agg[info.status] || 0) + 1;
                    }
                    const aggTxt = Object.entries(agg).map(([s, n]) => `${n} ${t("st." + s)}`).join(" · ");
                    const open = !!state.expanded[row.id];
                    return (
                      <React.Fragment key={row.id}>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              className="cb"
                              checked={chosen === selectable && selectable > 0}
                              disabled={selectable === 0}
                              onChange={(e) => onParent(row, e.target.checked)}
                            />
                          </td>
                          <td>
                            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <button
                                className={"exp-btn" + (open ? " open" : "")}
                                onClick={() => dispatch({ type: "TOGGLE_EXPAND", payload: row.id })}
                                aria-label="Werke aufklappen"
                              >
                                <svg className="ic ic-16"><use href="#ic-chevron-right" /></svg>
                              </button>
                              <span>
                                <span className="art-name">{row.name}</span><br />
                                <span className="art-id">{row.id} · {t("s3.plain", { n: visible.length })}</span>
                              </span>
                            </span>
                          </td>
                          <td>{row.type}</td>
                          <td>{row.srcVer}</td>
                          <td>-</td>
                          <td><span className="tiny muted">{aggTxt}</span></td>
                          <td />
                        </tr>
                        {open && visible.map((b) => {
                          const info = row.werkeStatus[b.key];
                          if (!info) return null;
                          const ident = info.status === "identical";
                          const sel = !!state.sel?.[row.id]?.[b.key];
                          return (
                            <tr className="childrow" key={row.id + "-" + b.key}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="cb"
                                  checked={sel}
                                  disabled={ident}
                                  onChange={(e) => onToggle(row.id, b.key, e.target.checked)}
                                />
                              </td>
                              <td>
                                <span className="tree-cell">
                                  <span>
                                    <span className="art-name">{b.name}</span>
                                    <span className="art-id" style={{ display: "block" }}>
                                      {row.id}<span className="ken">{b.kennung}</span> · {info.suffixedId}
                                    </span>
                                  </span>
                                </span>
                              </td>
                              <td>{row.type}</td>
                              <td>{row.srcVer}</td>
                              <td>{info.tgt || "-"}</td>
                              <td><StatusPill status={info.status} /></td>
                              <td>
                                {info.status === "update" && (
                                  <button className="link-act" onClick={() => openModal(row, b.key)}>{t("s3.view")}</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {refsMapped.length > 0 && (
            <div className="sect panel">
              <div className="panel-h">
                <h3>{t("s3.refs")}</h3>
                <span className="meta">{t("s3.refsMeta")}</span>
              </div>
              <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
                <table className="dt">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }} />
                      <th>{t("th.art")}</th>
                      <th>{t("th.type")}</th>
                      <th>{t("th.src")}</th>
                      <th>{t("th.tgt")}</th>
                      <th>{t("th.status")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {refsMapped.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="cb"
                            checked={r.resolvable && !!state.refSel[r.id]}
                            disabled={r.status === "identical" || !r.resolvable}
                            onChange={() => dispatch({ type: "TOGGLE_REF", payload: r.id })}
                          />
                        </td>
                        <td><span className="art-name">{r.name}</span></td>
                        <td>{r.type}</td>
                        <td>{r.srcVer}</td>
                        <td>{r.tgtVer}</td>
                        <td><StatusPill status={r.status} /></td>
                        <td>
                          {!r.resolvable && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refUnres")}</span>}
                          {r.resolvable && r.status === "identical" && <span className="tiny muted">{t("s3.ident")}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {stream.refsWarning && (
                  <div className="errline" style={{ marginTop: 8 }}>
                    <span style={{ flex: 1 }}>{stream.refsWarning}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 2 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span className="foot-hint">
            {canNext ? t("s3.selN", { n: selectedRowsCount }) : t("s3.min")}
          </span>
          <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 4 })} disabled={!canNext}>
            {t("s3.next")}
            <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
          </button>
        </div>
      </div>

      <BpmnCompareDialog
        open={!!modal}
        onClose={() => setModal(null)}
        sourceInstanceId={modal?.sourceInstanceId}
        targetInstanceId={modal?.targetInstanceId}
        artifactId={modal?.artId}
        targetArtifactId={modal?.targetArtId}
        artifactName={modal?.artName}
        sourcePackageId={state.pkgId}
        sourceVersion={modal?.srcVer}
        targetVersion={modal?.tgtVer}
      />
    </section>
  );
}
